import { NotificationBell } from '@/components/notifications/NotificationBell';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBars,
  faSun,
  faMoon,
  faUser,
  faBuilding,
  faShield,
} from '@fortawesome/free-solid-svg-icons';
import { useThemeContext } from '@/providers/ThemeProvider';
import { useAuthStore } from '@/stores/auth.store';
import { HeaderClock } from './HeaderClock';
import { useUiStore } from '@/stores/ui.store';
import { authService } from '@/services/auth.service';
import { useHasPermission } from '@/hooks/use-permissions';
import { AccountMenu } from '@/components/shared/AccountMenu';
import { ROUTES } from '@/routes/routes';

export function Header() {
  const { resolvedMode, setMode } = useThemeContext();
  const user = useAuthStore((s) => s.user);
  const refreshToken = useAuthStore((s) => s.refreshToken);
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const setMobileSidebarOpen = useUiStore((s) => s.setMobileSidebarOpen);
  const navigate = useNavigate();
  const can = useHasPermission();

  const displayName = user ? `${user.firstName} ${user.lastName}` : 'User';

  /**
   * D-05 — signing out must revoke the refresh token server-side, not just drop
   * it from the store. Without the API call the token stayed valid for its full
   * 7-day life after the user believed they had signed out; the customer portal
   * and platform console already did this correctly, the tenant header did not.
   *
   * Local state is cleared and the user is navigated away *regardless* of the
   * request's outcome — a revocation that fails must never leave someone
   * looking authenticated. Server-side `revoke()` is idempotent, so a token
   * that was already revoked or rotated is a harmless no-op.
   */
  const handleLogout = async () => {
    try {
      if (refreshToken) await authService.logout(refreshToken);
    } catch {
      // ignore — still clear local state
    }
    clearAuth();
    navigate(ROUTES.LOGIN);
  };

  return (
    <header className="relative flex h-16 shrink-0 items-center justify-between border-b border-border bg-background px-4 sm:px-6">
      {/* Mobile sidebar toggle */}
      <button
        onClick={() => setMobileSidebarOpen(true)}
        className="flex h-11 w-11 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-primary/[0.07] hover:text-foreground lg:hidden"
        aria-label="Open navigation"
      >
        <FontAwesomeIcon icon={faBars} className="h-4 w-4" />
      </button>

      {/* Workspace badge — desktop */}
      {user?.tenantName && (
        <div className="hidden items-center gap-1.5 rounded-lg border border-border/60 px-3 py-1.5 text-xs lg:flex">
          <FontAwesomeIcon icon={faBuilding} className="h-3 w-3 text-muted-foreground" />
          <span className="font-medium text-foreground">{user.tenantName}</span>
          <span className="text-muted-foreground">·</span>
          <span className="text-muted-foreground">{user.tenantSlug}</span>
          {user.roleName && (
            <>
              <span className="text-muted-foreground">·</span>
              <FontAwesomeIcon icon={faShield} className="h-3 w-3 text-primary" />
              <span className="text-primary font-medium">{user.roleName}</span>
            </>
          )}
        </div>
      )}

      <div className="hidden flex-1 lg:block" />

      {/* Absolutely centred, so it stays on true centre regardless of how wide
          the workspace badge or the right-hand controls become. */}
      <HeaderClock />

      <div className="flex items-center gap-2">
        {/* B6.17 — Notification Center */}
        <NotificationBell />

        {/* Theme toggle */}
        <button
          onClick={() => setMode(resolvedMode === 'dark' ? 'light' : 'dark')}
          className="flex h-11 w-11 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-primary/[0.07] hover:text-foreground sm:h-9 sm:w-9"
          aria-label={resolvedMode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          <FontAwesomeIcon icon={resolvedMode === 'dark' ? faSun : faMoon} className="h-4 w-4" />
        </button>

        {/* User Profile P1 — the shared account menu: photo avatar, My Profile,
            Settings (only when the permission allows), Sign out. */}
        {user && (
          <AccountMenu
            displayName={displayName}
            firstName={user.firstName}
            lastName={user.lastName}
            photoUrl={user.profilePhotoUrl ?? null}
            email={user.email}
            username={user.username}
            isSharedEmailAccount={!!user.isSharedEmailAccount}
            workspaceName={user.tenantName}
            workspaceSlug={user.tenantSlug}
            roleName={user.roleName}
            onProfile={() => navigate(ROUTES.PROFILE)}
            {...(can('company-profile:view')
              ? { onSettings: () => navigate(ROUTES.SETTINGS_COMPANY_PROFILE) }
              : {})}
            onLogout={() => void handleLogout()}
          />
        )}

        {!user && (
          <button
            onClick={() => navigate(ROUTES.LOGIN)}
            className="flex h-9 items-center gap-2 rounded-lg px-3 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <FontAwesomeIcon icon={faUser} className="h-4 w-4" />
            <span>Sign in</span>
          </button>
        )}
      </div>
    </header>
  );
}
